import React from "react";
import {
    Box,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    CircularProgress,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { visuallyHidden } from '@mui/utils';
import { enterpriseTokens } from "styles/theme";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        padding: "9px 12px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        padding: "10px 12px",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: "background-color 140ms ease",
    "&:last-child td, &:last-child th": {
        border: "0 !important",
    },
}));


const TableComponent = (props) => {
    const {
        data,
        columns,
        isLoading,
        order,
        orderBy,
        createSortHandler,
        onRowClick,
        selectedRowId,
        containerSx,
        stickyHeader = false
    } = props;

    const stickyLeftByIndex = [];
    let runningLeft = 0;
    columns?.forEach((col, index) => {
        if (col.sticky) {
            stickyLeftByIndex[index] = runningLeft;
            // handle "250px" or number
            let w = 0;
            if (typeof col.width === "number") {
                w = col.width;
            } else if (typeof col.width === "string") {
                const parsed = parseInt(col.width, 10);
                w = isNaN(parsed) ? 150 : parsed;
            } else {
                w = 150; // default
            }
            runningLeft += w;
        }
    });

    return (
        <>
            <TableContainer sx={containerSx}>
                <Table stickyHeader={stickyHeader} sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                    <TableHead>
                        <TableRow>
                            {columns?.map((col, index) =>
                                isLoading ? (
                                    <StyledTableCell key={`header-skeleton-${index}`}>
                                        <Skeleton variant="text" width={80} height={24} />
                                    </StyledTableCell>
                                ) :

                                    <StyledTableCell
                                        key={`${col?.name}-${index}`}
                                        sortDirection={orderBy === col.field ? order : false}
                                        sx={{
                                            whiteSpace: !col?.width && "nowrap",
                                            color: enterpriseTokens.slate,
                                            minWidth: col?.width || "auto",
                                            borderTop: `1px solid ${enterpriseTokens.lineSoft}`,
                                            borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
                                            backgroundColor: "#F4F7FB",
                                            zIndex: col.sticky || col.stickyRight ? 3 : 1,
                                            ...(col.sticky && {
                                                position: "sticky",
                                                left: stickyLeftByIndex[index] || 0,
                                                boxShadow: `inset -1px 0 0 ${enterpriseTokens.lineSoft}`,
                                            }),
                                            ...(col.stickyRight && {
                                                position: "sticky",
                                                right: 0,
                                                boxShadow: `inset 1px 0 0 ${enterpriseTokens.lineSoft}`,
                                            }),
                                        }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === col.field}
                                            direction={orderBy === col.field ? order : 'asc'}
                                            onClick={() => createSortHandler(col.field)}
                                        >
                                            {col.name}
                                            {orderBy === col.field ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    </StyledTableCell>

                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, i) => (
                                <StyledTableRow key={`skeleton-${i}`}>
                                    {columns?.map((_, j) => (
                                        <StyledTableCell key={j}>
                                            <Skeleton variant="text" width="100%" height={30} />
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            ))
                        ) : data?.length > 0 ? (
                            data?.map((row, index) => (
                                <StyledTableRow onClick={() => onRowClick ? onRowClick(row) : null} key={index} sx={{
                                    "&:hover": {
                                        backgroundColor: enterpriseTokens.surfaceTint,
                                    },
                                    "&:hover .hoverButtons": { display: "flex" },
                                    "&:hover .defaultIcon": { display: "none" },
                                    "&:not(:last-child) td, &:not(:last-child) th": {
                                        borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
                                    },
                                    "&:last-child td": {
                                        borderBottom: "none",
                                    },
                                    bgcolor: row?.id === selectedRowId && enterpriseTokens.surfaceTint,
                                    cursor: onRowClick && "pointer",
                                    "& th": { bgcolor: row.id === selectedRowId && "inherit" },
                                }}>
                                    {columns?.map((col, ind) =>
                                        <StyledTableCell
                                            key={`${col.name}-${ind}`}
                                            component="th"
                                            scope="row"
                                            sx={{
                                                whiteSpace: !col?.width && "nowrap",
                                                maxWidth: col?.width || "auto",
                                                color: enterpriseTokens.navySoft,
                                                backgroundColor: "inherit",
                                                zIndex: col.sticky || col.stickyRight ? 2 : 1,
                                                ...(col.sticky && {
                                                    position: "sticky",
                                                    left: stickyLeftByIndex[ind] || 0,
                                                    boxShadow: `inset -1px 0 0 ${enterpriseTokens.lineSoft}`,
                                                }),
                                                ...(col.stickyRight && {
                                                    position: "sticky",
                                                    right: 0,
                                                    boxShadow: `inset 1px 0 0 ${enterpriseTokens.lineSoft}`,
                                                }),
                                            }}
                                        >
                                            {col.renderer ? col.renderer(row, index) : row[col.field]}
                                        </StyledTableCell>
                                    )}
                                </StyledTableRow>
                            ))
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={columns?.length}>
                                    Record not found.
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default TableComponent;
